import { useMemo, ChangeEvent } from 'react';

import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import { useMediaQuery } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import { tablePaginationClasses } from '@mui/material/TablePagination';

import i18n from 'src/locales/i18n';
import { PageOptions } from 'src/services/api.types';
import { ICandidateProfileDocument } from 'src/services/candidates/candidates.types';
import { IJobDocument, ICompanyDocument } from 'src/services/connections/connections.types';
import { OrganisationDocument as IOrganisationDocument } from 'src/services/files/file.types';

import JobDocumentTableRow from 'src/components/documents/job-document-row';
import DealDocumentTableRow from 'src/components/documents/deal-document-row';
import {
  TableProps,
  TableNoData,
  TableSkeleton,
  TableHeadCustom,
  TablePaginationCustom,
} from 'src/components/table';

import FileManagerTableRow from './file-manager-table-row';
import CompanyDocumentTableRow from './company-profile-document-row';
import ProfileDocumentTableRow from './candidate-profile-document-row';

// ----------------------------------------------------------------------

type Props = {
  table: TableProps;
  notFound: boolean;
  isLoading?: boolean;
  dataFiltered: IOrganisationDocument[] | ICandidateProfileDocument[] | ICompanyDocument[] | IJobDocument[] | undefined;
  count: number;
  pageOptions?: PageOptions;
  onDeleteRow?: (id: string) => void;
  onSaveToProfile?: (id: string) => void;
  onSaveToLink?: (id: string) => void;
  onChangePage?: (_e: any, page: number) => void;
  onChangePerPage?: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onShareDocument?: (id: string) => void;
  /** Retitle a candidate-record document. Only honoured for `documentType="company_profile"`. */
  onRenameRow?: (id: string) => void;
  /** Save a candidate-record document under its title. Only honoured for `documentType="company_profile"`. */
  onDownloadRow?: (id: string) => void;
  documentType?: 'organisation' | 'candidate' | 'company_profile' | 'deal' | 'job';
  /**
   * Render the per-document guest sharing toggle. Only honoured for `documentType="candidate"`,
   * which is the application's document list; every other caller of this table is a record that
   * has no guest portal behind it.
   */
  showGuestSharing?: boolean;
};

export default function FileManagerTable({
  table,
  notFound,
  isLoading,
  onDeleteRow,
  onSaveToProfile,
  onSaveToLink,
  count,
  dataFiltered,
  pageOptions,
  onChangePage,
  onChangePerPage,
  onShareDocument,
  onRenameRow,
  onDownloadRow,
  documentType = 'organisation',
  showGuestSharing = false,
}: Props) {
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const tableLabels = useMemo(() => {
    switch (documentType) {
      case 'organisation':
        return [
          { id: 'name', label: i18n.t('common.name') },
          { id: 'type', label: i18n.t('common.type'), width: 120 },
          { id: 'modifiedAt', label: i18n.t('common.modified'), width: 180 },
          { id: 'popover', width: 80 }
        ];

      case 'candidate':
        return [
          { id: 'icon', label: i18n.t('common.preview'), width: undefined },
          { id: 'expiry_date', label: i18n.t('common.expiry'), width: 200 },
          { id: 'preview', label: i18n.t('common.preview'), width: 80 },
          ...(showGuestSharing
            ? [{ id: 'shared_with_guest', label: i18n.t('profile.documents.guest_sharing.column'), width: 150 }]
            : []),
          { id: 'popover', width: 80 }
        ];

      case 'company_profile':
        return [
          { id: 'name', label: i18n.t('common.name'), width: undefined },
          { id: 'icon', label: i18n.t('common.preview') },
          { id: 'modifiedAt', label: i18n.t('common.added') },
          { id: 'popover', width: 80 }
        ];
      case 'deal':
        return [
          { id: 'name', label: i18n.t('common.name'), width: undefined },
          { id: 'icon', label: i18n.t('common.preview') },
          { id: 'modifiedAt', label: i18n.t('common.added') },
          { id: 'popover', width: 80 }
        ];
      case 'job':
        return [
          { id: 'name', label: i18n.t('common.name') },
          { id: 'type', label: i18n.t('common.type'), width: 120 },
          { id: 'modifiedAt', label: i18n.t('common.modified'), width: 180 },
          { id: 'popover', width: 80 }
        ];
      default:
        return [
          { id: 'icon', label: i18n.t('common.preview'), width: undefined },
          { id: 'name', label: i18n.t('common.name') },
          { id: 'type', label: i18n.t('common.type'), width: 120 },
          { id: 'modifiedAt', label: i18n.t('common.modified'), width: 180 }
        ];
    }
  }, [documentType, showGuestSharing]);

  const {
    dense,
    //
    selected,
    onSelectRow,
    onChangeDense,
  } = table;

  const renderRow = (row: any) => {

    if (documentType === 'company_profile') {
      return <CompanyDocumentTableRow
        key={row.id}
        row={row as ICompanyDocument}
        selected={selected.includes(row.id)}
        onSelectRow={() => onSelectRow(row.id)}
        onDeleteRow={onDeleteRow ? () => onDeleteRow(row.id) : undefined}
        onRename={onRenameRow ? () => onRenameRow(row.id) : undefined}
        onDownload={onDownloadRow ? () => onDownloadRow(row.id) : undefined}
      />;
    }

    if (documentType === 'deal') {
      return <DealDocumentTableRow
        key={row.id}
        row={row as ICompanyDocument}
        selected={selected.includes(row.id)}
        onSaveToLink={onSaveToLink ? () => onSaveToLink(row.id) : undefined}
        onDeleteRow={onDeleteRow ? () => onDeleteRow(row.id) : undefined}
      />;
    }

    if (documentType === 'candidate') {
      return <ProfileDocumentTableRow
        key={row.id}
        row={row as ICandidateProfileDocument}
        selected={selected.includes(row.id)}
        showGuestSharing={showGuestSharing}
        onSaveToProfile={onSaveToProfile ? () => onSaveToProfile(row.document.id) : undefined}
        onSelectRow={() => onSelectRow(row.id)}
        onDeleteRow={onDeleteRow ? () => onDeleteRow(row.id) : undefined}
      />;
    }

    if (documentType === 'job') {
      return <JobDocumentTableRow
        key={row.id}
        row={row as IJobDocument}
        selected={selected.includes(row.id)}
        onShareDocument={onShareDocument ? () => onShareDocument(row.document.id) : undefined}
        onDeleteRow={onDeleteRow ? () => onDeleteRow(row.document.id) : undefined}
      />;
    }

    return <FileManagerTableRow
      key={row.id}
      row={row as IOrganisationDocument}
      selected={selected.includes(row.id)}
      onSelectRow={() => onSelectRow(row.id)}
    />
  }

  return (
    <>
      {isLoading ? (
        <TableSkeleton />
      ) : (
        <>
          <Box
            sx={{
              position: 'relative',
            }}
          >
            <TableContainer>
              <Table
                size={dense ? 'small' : 'medium'}
                sx={{
                  borderCollapse: 'separate',
                }}
              >
                {!isMobile && (
                  <TableHeadCustom headLabel={tableLabels} rowCount={dataFiltered?.length} />
                )}

                <TableBody>
                  {dataFiltered?.map((row) =>
                    renderRow(row)
                  )}

                  <TableNoData notFound={notFound} />
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {pageOptions && onChangePage && onChangePerPage && (
            <TablePaginationCustom
              count={count}
              page={pageOptions.page}
              rowsPerPage={pageOptions.per_page}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangePerPage}
              //
              dense={dense}
              onChangeDense={onChangeDense}
              sx={{
                [`& .${tablePaginationClasses.toolbar}`]: {
                  borderTopColor: 'transparent',
                },
              }}
            />
          )}
        </>
      )
      }
    </>
  );
}
